import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Search,
  Users,
  X,
} from "lucide-react";

import { attachContactsToMyPairs } from "./myRelationshipsDataAdapter";
import MyRelationshipsEmptyState from "./MyRelationshipsEmptyState";
import MyRelationshipsL0Map from "./MyRelationshipsL0Map";
import MyRelationshipsL1CompareMap from "./MyRelationshipsL1CompareMap";
import {
  EXPLORE_ALL_RELATIONSHIPS_COMING_SOON_MSG,
  EXPLORE_ALL_RELATIONSHIPS_ENABLED,
} from "./relationshipTabAccess";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { getCustomerContactAvatarBg } from "../../../Utils/Helper/profileAvatarUi";
import {
  fanCapColor,
  fanCapTone,
  resolveOwnerCapacities,
} from "../../../Utils/Helper/rlFanCaps";
import { AccountAlRlPills } from "../account-relationships/account-al-rl-pills";
import { AccountPickerCard } from "../account-relationships/account-picker-card";
import { useRole } from "../account-relationships/RelationshipContexts";
import {
  formatLevel,
  formatMeddKeyDisplay,
  formatMeddRoleLabelDisplay,
  getAccountIdentityBadgeStyle,
  getAccountIdentityColor,
  getAccountStatus,
  getInitials,
  getRlBadgeClasses,
  getStakeholderGradeLevel,
  resolveContactMeddpiccMatrixMeta,
} from "../account-relationships/relationshipUtils";
import { RlAssessmentModal } from "../account-relationships/rl-assessment-modal";

const exploreAllRelationshipsBtnStyle = EXPLORE_ALL_RELATIONSHIPS_ENABLED
  ? undefined
  : {
      opacity: 0.45,
      cursor: "not-allowed",
      filter: "grayscale(0.35)",
    };

function resolveAccountContact(account, contactId) {
  return (
    (account?.contacts || []).find(
      item => String(item.id) === String(contactId)
    ) || null
  );
}

function getContactMeddpiccMeta(contact, considerMeddpiccRole) {
  if (!considerMeddpiccRole || !contact) return null;
  const { meddKey, meddRoleLabel } = resolveContactMeddpiccMatrixMeta(contact);
  return {
    meddKey,
    displayKey: formatMeddKeyDisplay(meddKey),
    displayLabel: formatMeddRoleLabelDisplay(meddRoleLabel),
  };
}

function renderSidebarContactInfo({
  contact,
  pair,
  considerMeddpiccRole,
  meddMeta,
}) {
  const subtitle =
    pair?.relationshipGoal || contact?.title || "Customer Contact";
  const positionLine = contact?.title || "";
  const meddKeyClass = meddMeta?.meddKey
    ? `medd medd-${String(meddMeta.meddKey).toLowerCase()}`
    : "";

  if (considerMeddpiccRole) {
    const titleLine = positionLine || subtitle;

    return (
      <div className="t3-sb-contact-info">
        <div className="t3-sb-contact-name t3-sb-contact-name--with-medd">
          <span className="t3-sb-contact-name-text">
            {contact?.name || "Contact"}
          </span>
          {meddMeta ? (
            <span className={meddKeyClass} title={meddMeta.displayLabel}>
              {meddMeta.displayKey}
            </span>
          ) : null}
        </div>
        <div className="t3-sb-contact-title">{titleLine}</div>
      </div>
    );
  }

  return (
    <div className="t3-sb-contact-info">
      <div className="t3-sb-contact-name">{contact?.name || "Contact"}</div>
      <div className="t3-sb-contact-title">{subtitle}</div>
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [12, 24, 48];
const COMPARE_LIMIT = 8;
const AUTO_PICK_OPTIONS = [
  { value: "weak", label: "Weak (RL 0–1)" },
  { value: "moderate", label: "Moderate (RL 2–3)" },
  { value: "strong", label: "Strong (RL 4–5)" },
  { value: "heavy", label: "Heavy mapping (3+)" },
];

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function getAccountSearchText(account) {
  return [
    account?.name,
    account?.industry,
    account?.tier,
    account?.region,
    getAccountStatus(account),
    ...(account?.contacts || []).map(
      contact => `${contact.name} ${contact.title}`
    ),
  ].join(" ");
}

function computePortfolioStats(
  scopePairs,
  { comparisonActive = false, selectedAccountIds = [], accountsCount = 0 } = {}
) {
  const pending = scopePairs.filter(
    pair => pair.rlLevel === null || pair.rlLevel === undefined
  ).length;
  const uniqueContacts = new Set(
    scopePairs.map(pair => `${pair.account.id}:${pair.contactId}`)
  ).size;
  const assessedPairs = scopePairs.filter(
    pair => pair.rlLevel !== null && pair.rlLevel !== undefined
  );
  const strongPairs = assessedPairs.filter(
    pair => Number(pair.rlLevel) >= 4
  ).length;
  const atRiskPairs = assessedPairs.filter(
    pair => Number(pair.rlLevel) <= 1
  ).length;

  return {
    accounts: comparisonActive ? selectedAccountIds.length : accountsCount,
    contactsMapped: uniqueContacts || scopePairs.length,
    totalPairs: scopePairs.length,
    assessed: assessedPairs.length,
    strongPairs,
    atRiskPairs,
    pending,
    orphans: 0,
  };
}

export default function MyRelationshipsTab({
  accounts = [],
  currentUserStakeholder,
  executiveId,
  loggedInUserId,
  portfolioOwnerFirstName = "",
  onSelectAccount,
  onExploreAllRelationships,
  onRefreshMappings,
  teamsRelationshipToggle = false,
  onTeamsRelationshipToggle,
  teamsSelectedUserName,
  teamsSelectedUserInfo = null,
  teamMemberAccountIds = [],
  teamsSelectedUserId,
  onOpenTeamModal,
  viewMyGraph = false,
  onToggleViewMyGraph,
  teamMemberGraphAccounts = [],
  teamMemberGraphCenterStakeholder = null,
}) {
  const { considerMeddpiccRole, orgSettings } = useRole();
  const [mode, setMode] = useState("portfolio");
  const [sortBy, setSortBy] = useState("aggregate_rl");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);
  const [peekAccountId, setPeekAccountId] = useState(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [comparisonActive, setComparisonActive] = useState(false);
  const [focusedCompareAccountId, setFocusedCompareAccountId] = useState(null);
  const [autoPick, setAutoPick] = useState("weak");
  const [autoPickNote, setAutoPickNote] = useState("");
  const [assessmentTarget, setAssessmentTarget] = useState(null);
  const cardsScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const portfolioAccounts = accounts;
  const relationshipUserName = useMemo(() => {
    if (!executiveId || executiveId === loggedInUserId) return "";
    return (
      currentUserStakeholder?.name ||
      portfolioAccounts.find(account => account.currentUserStakeholder)
        ?.currentUserStakeholder?.name ||
      ""
    );
  }, [currentUserStakeholder, executiveId, loggedInUserId, portfolioAccounts]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy, pageSize, portfolioAccounts]);

  const sortedAccounts = useMemo(() => {
    const query = normalizeText(searchQuery);
    const list = [...(portfolioAccounts || [])].filter(account => {
      if (!query) return true;
      return normalizeText(getAccountSearchText(account)).includes(query);
    });

    if (sortBy === "aggregate_rl") {
      list.sort((a, b) => {
        const ra = a.rlAggregate;
        const rb = b.rlAggregate;
        if (ra == null && rb == null) return 0;
        if (ra == null) return 1;
        if (rb == null) return -1;
        return Number(rb) - Number(ra);
      });
    } else if (sortBy === "name") {
      list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    } else if (sortBy === "fanout") {
      list.sort(
        (a, b) =>
          (Number(b.myMappedPairs) || 0) - (Number(a.myMappedPairs) || 0)
      );
    } else if (sortBy === "pending") {
      list.sort(
        (a, b) =>
          (Number(b.myPendingAssessments) || 0) -
          (Number(a.myPendingAssessments) || 0)
      );
    }
    return list.map(account => ({
      ...account,
      sectorColor: getAccountIdentityColor(account.id),
    }));
  }, [portfolioAccounts, sortBy, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(sortedAccounts.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageAccounts = sortedAccounts.slice(pageStart, pageStart + pageSize);

  const updateCardsScrollState = useCallback(() => {
    const el = cardsScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(maxScroll > 2 && el.scrollLeft < maxScroll - 2);
  }, []);

  const scrollAccountCards = useCallback(direction => {
    const el = cardsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 240, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const el = cardsScrollRef.current;
    if (!el) return undefined;
    updateCardsScrollState();
    el.addEventListener("scroll", updateCardsScrollState, { passive: true });
    const ro = new ResizeObserver(updateCardsScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateCardsScrollState);
      ro.disconnect();
    };
  }, [
    sortedAccounts.length,
    selectedAccountIds.length,
    comparisonActive,
    searchQuery,
    updateCardsScrollState,
  ]);

  const scrollStripAccountIntoView = useCallback(accountId => {
    if (!accountId) return;
    const card = cardsScrollRef.current?.querySelector(
      `[data-acct-card="${accountId}"]`
    );
    card?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, []);

  useEffect(() => {
    if (!peekAccountId) return;
    const accountIndex = sortedAccounts.findIndex(
      account => account.id === peekAccountId
    );
    if (accountIndex < 0) return;
    const accountPage = Math.floor(accountIndex / pageSize) + 1;
    if (accountPage !== safePage) setPage(accountPage);
  }, [peekAccountId, sortedAccounts, pageSize, safePage]);

  const isViewingTeamMember =
    teamsRelationshipToggle && Boolean(teamsSelectedUserName);
  const teamGraphActive = isViewingTeamMember && !viewMyGraph;

  const myPairs = useMemo(() => {
    // Team Rel graph (View My Graph OFF): use ONLY teammate's accounts/myPairs.
    // Do NOT fall back to logged-in user's pairs for accounts where the teammate
    // isn't mapped — that inflates stats when viewing their lens.
    if (teamGraphActive && teamMemberGraphAccounts.length > 0) {
      return teamMemberGraphAccounts.flatMap(account => {
        const color =
          portfolioAccounts.find(a => String(a.id) === String(account.id))
            ?.sectorColor || account.sectorColor;
        return (account.myPairs || []).map(pair => ({
          ...pair,
          account: { ...account, sectorColor: color },
        }));
      });
    }
    // Default: logged-in user's portfolio
    return portfolioAccounts.flatMap(account =>
      (account.myPairs || []).map(pair => ({ ...pair, account }))
    );
  }, [portfolioAccounts, teamGraphActive, teamMemberGraphAccounts]);

  const comparisonPairs = useMemo(
    () =>
      comparisonActive
        ? myPairs.filter(pair => selectedAccountIds.includes(pair.account.id))
        : myPairs,
    [comparisonActive, myPairs, selectedAccountIds]
  );

  const stats = useMemo(() => {
    // When viewing a team member's lens (teamGraphActive), use their account count
    const lensAccountsCount =
      teamGraphActive && teamMemberGraphAccounts.length > 0
        ? teamMemberGraphAccounts.length
        : portfolioAccounts.length;
    return computePortfolioStats(comparisonPairs, {
      comparisonActive,
      selectedAccountIds,
      accountsCount: lensAccountsCount,
    });
  }, [
    comparisonPairs,
    comparisonActive,
    selectedAccountIds,
    portfolioAccounts.length,
    teamGraphActive,
    teamMemberGraphAccounts.length,
  ]);

  const teamMemberCenterStakeholder = useMemo(() => {
    if (teamMemberGraphCenterStakeholder) {
      return teamMemberGraphCenterStakeholder;
    }
    if (teamsSelectedUserInfo) {
      return {
        name: teamsSelectedUserInfo.user_name || teamsSelectedUserName || "",
        title: teamsSelectedUserInfo.title || teamsSelectedUserInfo.role || "",
        ladderLevel: getStakeholderGradeLevel({
          ladderLevel: teamsSelectedUserInfo.grade,
          raw: teamsSelectedUserInfo,
        }),
        raw: teamsSelectedUserInfo,
      };
    }
    return teamsSelectedUserName
      ? { name: teamsSelectedUserName, title: "", raw: {} }
      : null;
  }, [
    teamMemberGraphCenterStakeholder,
    teamsSelectedUserInfo,
    teamsSelectedUserName,
  ]);

  const centerStakeholder = teamGraphActive
    ? teamMemberCenterStakeholder
    : currentUserStakeholder;
  const viewerGrade = getStakeholderGradeLevel(centerStakeholder);
  const ownerCaps = useMemo(
    () =>
      resolveOwnerCapacities({
        fanOutCaps: orgSettings?.fan_out_caps,
        overallFanOutCap: orgSettings?.overall_fan_out_cap,
        grade: viewerGrade,
        roleName:
          centerStakeholder?.title ||
          centerStakeholder?.raw?.role ||
          centerStakeholder?.raw?.coverage_role ||
          null,
      }),
    [
      orgSettings?.fan_out_caps,
      orgSettings?.overall_fan_out_cap,
      viewerGrade,
      centerStakeholder,
    ]
  );
  const fanOutCap = ownerCaps.local;
  const fanOutPortfolioCap = ownerCaps.global;

  const sharedAccounts = useMemo(
    () =>
      isViewingTeamMember
        ? sortedAccounts.filter(a => teamMemberAccountIds.includes(a.id))
        : [],
    [isViewingTeamMember, sortedAccounts, teamMemberAccountIds]
  );

  /**
   * Shared-account lens: teammate myPairs/counts when Team Rel graph is active
   * (View My Graph OFF); logged-in user payloads when View My Graph is ON.
   */
  const sharedAccountsForLens = useMemo(() => {
    if (!teamGraphActive || teamMemberGraphAccounts.length === 0) {
      return sharedAccounts;
    }
    return sharedAccounts.map(account => {
      const teammateVersion = teamMemberGraphAccounts.find(
        item => String(item.id) === String(account.id)
      );
      if (!teammateVersion) return account;
      return {
        ...teammateVersion,
        sectorColor:
          account.sectorColor ||
          teammateVersion.sectorColor ||
          getAccountIdentityColor(account.id),
      };
    });
  }, [teamGraphActive, teamMemberGraphAccounts, sharedAccounts]);

  /** Map / peek shared set — same lens as picker cards. */
  const teamGraphSharedAccounts = sharedAccountsForLens;

  const teamViewerFirstName = useMemo(() => {
    if (!teamGraphActive) return "";
    return (teamsSelectedUserName || "").trim().split(/\s+/)[0] || "Their";
  }, [teamGraphActive, teamsSelectedUserName]);

  const assessmentExecutiveId =
    teamGraphActive && teamsSelectedUserId ? teamsSelectedUserId : executiveId;

  const myOnlyAccounts = useMemo(
    () =>
      isViewingTeamMember
        ? sortedAccounts.filter(a => !teamMemberAccountIds.includes(a.id))
        : [],
    [isViewingTeamMember, sortedAccounts, teamMemberAccountIds]
  );

  const sharedAccountIds = useMemo(
    () => sharedAccounts.map(a => a.id),
    [sharedAccounts]
  );

  // Scenario 2: teammate graph — only shared (green) accounts are interactive.
  // Scenario 1 & 3: all portfolio accounts remain clickable.
  const restrictToSharedAccounts = teamGraphActive;
  const isAccountAccessible = useCallback(
    accountId =>
      !restrictToSharedAccounts || sharedAccountIds.includes(accountId),
    [restrictToSharedAccounts, sharedAccountIds]
  );

  useEffect(() => {
    if (!restrictToSharedAccounts) return;
    setSelectedAccountIds(current => {
      const next = current.filter(id => sharedAccountIds.includes(id));
      return next.length === current.length ? current : next;
    });
    setPeekAccountId(current =>
      current && !sharedAccountIds.includes(current) ? null : current
    );
    setFocusedCompareAccountId(current =>
      current && !sharedAccountIds.includes(current) ? null : current
    );
  }, [restrictToSharedAccounts, sharedAccountIds]);

  const openAssessmentForPair = useCallback(
    (account, pair, event) => {
      event?.stopPropagation?.();
      if (!pair?.contact) return;
      const stakeholder =
        pair.stakeholder ||
        account.currentUserStakeholder ||
        (teamGraphActive
          ? teamMemberCenterStakeholder
          : currentUserStakeholder);
      setAssessmentTarget({
        pair,
        stakeholder,
        contact: pair.contact,
        accountId: account.id,
      });
    },
    [currentUserStakeholder, teamGraphActive, teamMemberCenterStakeholder]
  );

  const handleAssessRingContact = useCallback(
    (account, ringContact) => {
      if (!ringContact?.pair || !ringContact?.contact) return;
      const stakeholder =
        ringContact.pair.stakeholder ||
        account.currentUserStakeholder ||
        (teamGraphActive
          ? teamMemberCenterStakeholder
          : currentUserStakeholder);
      setAssessmentTarget({
        pair: ringContact.pair,
        stakeholder,
        contact: ringContact.contact,
        accountId: account.id,
      });
    },
    [currentUserStakeholder, teamGraphActive, teamMemberCenterStakeholder]
  );

  const handleAssessmentSubmitted = useCallback(async () => {
    await onRefreshMappings?.();
    setAssessmentTarget(null);
  }, [onRefreshMappings]);

  const peekAccount = useMemo(() => {
    if (!peekAccountId) return null;
    if (teamGraphActive && teamMemberGraphAccounts.length > 0) {
      return (
        teamMemberGraphAccounts.find(account => account.id === peekAccountId) ||
        portfolioAccounts.find(account => account.id === peekAccountId) ||
        null
      );
    }
    return (
      portfolioAccounts.find(account => account.id === peekAccountId) || null
    );
  }, [
    peekAccountId,
    teamGraphActive,
    teamMemberGraphAccounts,
    portfolioAccounts,
  ]);
  const selectedAccounts = selectedAccountIds
    .map(
      id =>
        sortedAccounts.find(account => account.id === id) ||
        portfolioAccounts.find(account => account.id === id)
    )
    .filter(Boolean);
  const compareAccounts = useMemo(
    () =>
      selectedAccounts.map(account => ({
        ...account,
        sectorColor: getAccountIdentityColor(account.id),
      })),
    [selectedAccounts]
  );
  const compareAccountsResolved = useMemo(() => {
    if (!teamGraphActive || teamMemberGraphAccounts.length === 0) {
      return compareAccounts;
    }
    return compareAccounts.map(account => {
      const spVersion = teamMemberGraphAccounts.find(a => a.id === account.id);
      return spVersion
        ? { ...spVersion, sectorColor: account.sectorColor }
        : account;
    });
  }, [teamGraphActive, teamMemberGraphAccounts, compareAccounts]);
  const compareCenterLabel = teamGraphActive
    ? teamViewerFirstName || "SP"
    : "You";
  const stripAccounts = comparisonActive
    ? compareAccountsResolved
    : sortedAccounts;
  const compareMappedStatsLabel = teamGraphActive
    ? `${teamViewerFirstName || "Their"} mapped`
    : "your mapped";
  const focusedCompareAccount = (
    teamGraphActive ? compareAccountsResolved : compareAccounts
  ).find(account => account.id === focusedCompareAccountId);
  let fanOutScopeAccounts = portfolioAccounts;
  if (comparisonActive) {
    fanOutScopeAccounts = teamGraphActive
      ? compareAccountsResolved
      : compareAccounts;
  } else if (teamGraphActive && teamMemberGraphAccounts.length > 0) {
    // Use the team member's full portfolio for fan-out calculation (not just shared)
    fanOutScopeAccounts = teamMemberGraphAccounts;
  }
  const fanOutHighlightAccount = focusedCompareAccount || peekAccount;
  const fanOutCount = fanOutHighlightAccount
    ? Number(fanOutHighlightAccount.myMappedPairs) || 0
    : fanOutScopeAccounts.reduce(
        (max, account) => Math.max(max, Number(account.myMappedPairs) || 0),
        0
      );
  const fanOutTone = fanCapTone(fanOutCount, fanOutCap);
  const fanOutScopeLabel = fanOutHighlightAccount
    ? "this account"
    : teamGraphActive
      ? `${teamViewerFirstName || "their"} max / account`
      : "max / account";
  const visibleStart = sortedAccounts.length === 0 ? 0 : pageStart + 1;
  const visibleEnd = Math.min(pageStart + pageSize, sortedAccounts.length);
  const isCompareMode = mode === "compare";

  const toggleSelection = accountId => {
    if (!isAccountAccessible(accountId)) return;
    setPeekAccountId(null);
    setSelectedAccountIds(current => {
      if (current.includes(accountId))
        return current.filter(id => id !== accountId);
      if (current.length >= COMPARE_LIMIT) return current;
      return [...current, accountId];
    });
    setMode("compare");
    setComparisonActive(false);
  };

  const handleCompareFocus = useCallback(accountId => {
    setFocusedCompareAccountId(accountId);
    setPeekAccountId(null);
  }, []);

  useEffect(() => {
    const targetId = comparisonActive ? focusedCompareAccountId : peekAccountId;
    if (!targetId) return undefined;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollStripAccountIntoView(targetId));
    });
    return undefined;
  }, [
    comparisonActive,
    focusedCompareAccountId,
    peekAccountId,
    sortedAccounts.length,
    scrollStripAccountIntoView,
  ]);

  const handleAccountIntent = (accountId, event) => {
    if (!isAccountAccessible(accountId)) return;
    if (comparisonActive) {
      handleCompareFocus(
        focusedCompareAccountId === accountId ? null : accountId
      );
      return;
    }
    const isMultiSelect =
      isCompareMode || event?.shiftKey || event?.metaKey || event?.ctrlKey;
    if (isMultiSelect) {
      toggleSelection(accountId);
      return;
    }
    setPeekAccountId(current => (current === accountId ? null : accountId));
  };

  const clearSelection = () => {
    setSelectedAccountIds([]);
    setComparisonActive(false);
    setFocusedCompareAccountId(null);
    setAutoPickNote("");
  };

  const toggleCompareMode = () => {
    setMode(current => {
      const nextMode = current === "compare" ? "portfolio" : "compare";
      if (nextMode === "compare") setPeekAccountId(null);
      if (nextMode === "portfolio") {
        setSelectedAccountIds([]);
        setComparisonActive(false);
      }
      return nextMode;
    });
  };

  const applyAutoPick = criterion => {
    setAutoPick(criterion);
    const selectableAccounts = restrictToSharedAccounts
      ? sortedAccounts.filter(account => sharedAccountIds.includes(account.id))
      : sortedAccounts;
    const rows = selectableAccounts
      .map(account => ({
        account,
        rl:
          account.rlAggregate === null || account.rlAggregate === undefined
            ? null
            : Number(account.rlAggregate),
        mapped: Number(account.myMappedPairs) || 0,
      }))
      .filter(({ account }) => account.id);
    let candidates = [];
    if (criterion === "weak")
      candidates = rows
        .filter(row => row.rl !== null && row.rl < 2)
        .sort((a, b) => a.rl - b.rl);
    if (criterion === "moderate")
      candidates = rows
        .filter(row => row.rl !== null && row.rl >= 2 && row.rl < 4)
        .sort((a, b) => a.rl - b.rl);
    if (criterion === "strong")
      candidates = rows
        .filter(row => row.rl !== null && row.rl >= 4)
        .sort((a, b) => b.rl - a.rl);
    if (criterion === "heavy")
      candidates = rows
        .filter(row => row.mapped >= 3)
        .sort((a, b) => b.mapped - a.mapped);

    const picked = candidates
      .slice(0, COMPARE_LIMIT)
      .map(row => row.account.id);
    setSelectedAccountIds(picked);
    setPeekAccountId(null);
    setComparisonActive(false);
    setMode("compare");
    setAutoPickNote(
      picked.length === 0
        ? `No accounts match ${AUTO_PICK_OPTIONS.find(option => option.value === criterion)?.label || criterion}`
        : ""
    );
  };

  const renderAccountDetail = account => {
    if (!account) return null;
    const pairRows = attachContactsToMyPairs({
      ...account,
      currentUserStakeholder:
        account.currentUserStakeholder ||
        (teamGraphActive
          ? teamMemberCenterStakeholder
          : currentUserStakeholder),
    });
    const color = getAccountIdentityColor(account.id);
    const mappedToLabel = teamGraphActive
      ? teamViewerFirstName || "them"
      : "you";
    const contactsGroupLabel = teamGraphActive
      ? teamViewerFirstName
        ? `${teamViewerFirstName}'s contacts`
        : "Their contacts"
      : "Your contacts";
    const emptyContactsMsg = teamGraphActive
      ? `No contacts mapped to ${teamViewerFirstName || "this teammate"} at this account yet.`
      : "No contacts mapped to you at this account yet. Map them in the Coverage Matrix.";

    return (
      <div>
        <div className="t3-sb-acct-hdr">
          <div
            className="t3-sb-acct-logo"
            style={getAccountIdentityBadgeStyle(account.id, { color })}
          >
            {account.initials?.slice(0, 2) || "?"}
          </div>
          <div className="t3-sb-acct-name">{account.name}</div>
          <AccountAlRlPills account={account} layout="row" />
          <button
            type="button"
            className="t3-l2-close"
            onClick={() => setPeekAccountId(null)}
            aria-label="Close account peek"
          >
            x
          </button>
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: "var(--text-muted)",
            marginBottom: 8,
          }}
        >
          {pairRows.length} contact{pairRows.length === 1 ? "" : "s"} mapped to{" "}
          {mappedToLabel} · {account.myAssessedPairs || 0} assessed
        </div>
        <div className="t3-sb-group-lbl">{contactsGroupLabel}</div>
        {pairRows.length === 0 ? (
          <div
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              padding: "8px 4px",
              fontStyle: "italic",
            }}
          >
            {emptyContactsMsg}
          </div>
        ) : (
          pairRows.map(pair => {
            const contact =
              resolveAccountContact(account, pair.contact?.id) || pair.contact;
            const isPending =
              pair.rlLevel === null || pair.rlLevel === undefined;
            const meddMeta = getContactMeddpiccMeta(
              contact,
              considerMeddpiccRole
            );

            return (
              <div
                key={pair.id}
                className="t3-sb-contact-row"
                onClick={() => openAssessmentForPair(account, pair)}
                title={
                  isPending
                    ? "Assess this relationship"
                    : "Update RL assessment"
                }
                style={{ cursor: "pointer" }}
              >
                <div
                  className="t3-sb-avatar"
                  style={{ background: getCustomerContactAvatarBg() }}
                >
                  {getInitials(contact?.name || "Contact")}
                </div>
                {renderSidebarContactInfo({
                  contact,
                  pair,
                  considerMeddpiccRole,
                  meddMeta,
                })}
                <button
                  type="button"
                  className={`rounded-full border px-2 py-0.5 text-xs font-bold shrink-0 ${isPending ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100" : getRlBadgeClasses(pair.rlLevel)}`}
                  onClick={event => openAssessmentForPair(account, pair, event)}
                  title={
                    isPending
                      ? "Assess this relationship"
                      : "Update RL assessment"
                  }
                >
                  {isPending ? "Assess" : formatLevel("RL", pair.rlLevel)}
                </button>
              </div>
            );
          })
        )}
        {(account.contacts || []).filter(
          contact =>
            !pairRows.some(
              pair => String(pair.contact?.id) === String(contact.id)
            )
        ).length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 14,
                marginBottom: 4,
              }}
            >
              <div className="t3-sb-group-lbl" style={{ margin: 0 }}>
                Other contacts at this account
              </div>
            </div>
            {(account.contacts || [])
              .filter(
                contact =>
                  !pairRows.some(
                    pair => String(pair.contact?.id) === String(contact.id)
                  )
              )
              .map(contact => {
                const meddMeta = getContactMeddpiccMeta(
                  contact,
                  considerMeddpiccRole
                );

                return (
                  <div
                    key={contact.id}
                    className="t3-sb-contact-row"
                    style={{
                      opacity: 0.6,
                      cursor: "default",
                      background: "#fafbfc",
                    }}
                  >
                    <div
                      className="t3-sb-avatar"
                      style={{ background: getCustomerContactAvatarBg() }}
                    >
                      {getInitials(contact.name || "Contact")}
                    </div>
                    {renderSidebarContactInfo({
                      contact,
                      considerMeddpiccRole,
                      meddMeta,
                    })}
                    <span
                      style={{
                        fontSize: 9.5,
                        color: "var(--text-muted)",
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Not mapped
                    </span>
                  </div>
                );
              })}
          </>
        )}
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <button
            type="button"
            className="btn btn-sm btn-sec"
            onClick={() => onSelectAccount?.(account.id)}
            style={{ width: "100%", fontSize: 11 }}
          >
            View Account Relationships
          </button>
        </div>
      </div>
    );
  };

  const renderComparisonSidebar = () => {
    const scopedAccounts = compareAccountsResolved;
    const scopedPairs = scopedAccounts.flatMap(account =>
      (account.myPairs || []).map(pair => ({ ...pair, account }))
    );
    const assessedCount = scopedPairs.filter(
      pair => pair.rlLevel !== null && pair.rlLevel !== undefined
    ).length;
    const strongCount = scopedPairs.filter(
      pair => Number(pair.rlLevel) >= 4
    ).length;
    const atRiskCount = scopedPairs.filter(
      pair =>
        pair.rlLevel !== null &&
        pair.rlLevel !== undefined &&
        Number(pair.rlLevel) <= 1
    ).length;
    return (
      <>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "var(--gray-800)",
            marginBottom: 4,
          }}
        >
          Comparison overview
        </div>
        <div
          style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.6 }}
        >
          {scopedAccounts.length} account
          {scopedAccounts.length === 1 ? "" : "s"} · {scopedPairs.length} pair
          {scopedPairs.length === 1 ? "" : "s"} · {assessedCount} assessed ·{" "}
          {strongCount} strong (RL 4+)
          {atRiskCount ? (
            <span style={{ color: "var(--red)" }}>
              {" "}
              · {atRiskCount} at risk
            </span>
          ) : null}
        </div>
        <div className="t3-sb-section" style={{ marginTop: 12 }}>
          <div className="t3-sb-section-title">RL legend</div>
          <div
            style={{
              fontSize: 10.5,
              color: "var(--text-muted)",
              lineHeight: 1.7,
            }}
          >
            <div>
              <span style={{ color: "#22c55e", fontWeight: 700 }}>RL 4–5</span>{" "}
              Strong
            </div>
            <div>
              <span style={{ color: "#eab308", fontWeight: 700 }}>RL 2–3</span>{" "}
              Moderate
            </div>
            <div>
              <span style={{ color: "#ef4444", fontWeight: 700 }}>RL 0–1</span>{" "}
              At risk
            </div>
            <div>
              <span style={{ color: "#94a3b8", fontWeight: 700 }}>Grey</span> RL
              ?
            </div>
          </div>
        </div>
        <div className="t3-sb-section">
          <div className="t3-sb-section-title">Selected accounts</div>
          {scopedAccounts.map(account => (
            <button
              type="button"
              key={account.id}
              className={`t3-l0-card${focusedCompareAccountId === account.id ? " selected" : ""}`}
              onClick={() =>
                handleCompareFocus(
                  focusedCompareAccountId === account.id ? null : account.id
                )
              }
            >
              <div
                className="t3-l0-card-logo"
                style={getAccountIdentityBadgeStyle(account.id)}
              >
                {account.initials?.slice(0, 2) || "?"}
              </div>
              <div className="t3-l0-card-body">
                <div className="t3-l0-card-name">{account.name}</div>
                <div className="t3-l0-card-stats">
                  <span>
                    <b>{account.myMappedPairs || 0}</b>{" "}
                    {compareMappedStatsLabel}
                  </span>
                  <span>
                    <b>{account.myAssessedPairs || 0}</b> assessed
                  </span>
                  <span>
                    <b>{account.myPendingAssessments || 0}</b> pending
                  </span>
                </div>
              </div>
              <AccountAlRlPills account={account} />
            </button>
          ))}
        </div>
        <div
          style={{ marginTop: 16, fontSize: 11, color: "var(--text-muted)" }}
        >
          Click an account to focus its sector on the map and see contacts in
          the sidebar. Click again to clear focus.
        </div>
      </>
    );
  };

  const renderPortfolioSidebar = () => {
    const sidebarSharedAccounts = isViewingTeamMember
      ? sharedAccountsForLens
      : [];
    const sidebarOtherAccounts = isViewingTeamMember
      ? pageAccounts.filter(account => !sharedAccountIds.includes(account.id))
      : pageAccounts;

    const renderSidebarAccountRow = (
      account,
      { statsLabel = "your mapped" } = {}
    ) => {
      const color = getAccountIdentityColor(account.id);
      const mappedCount =
        Number(account.myMappedPairs ?? account.mappedPairs) || 0;
      const fanTone = fanCapTone(mappedCount, fanOutCap);
      const capWarn = fanTone === "ok" ? "" : "!";
      const inaccessible = !isAccountAccessible(account.id);
      return (
        <button
          type="button"
          key={account.id}
          className={`t3-l0-card${selectedAccountIds.includes(account.id) ? " selected" : ""}${peekAccountId === account.id ? " peek" : ""}`}
          onClick={event => handleAccountIntent(account.id, event)}
          disabled={inaccessible}
          aria-disabled={inaccessible ? "true" : undefined}
          title={
            inaccessible
              ? `${account.name} — only shared team accounts are available while viewing the teammate graph`
              : undefined
          }
          style={
            inaccessible
              ? {
                  opacity: 0.45,
                  cursor: "not-allowed",
                  filter: "grayscale(0.4)",
                }
              : undefined
          }
        >
          <div
            className="t3-l0-card-logo"
            style={getAccountIdentityBadgeStyle(account.id, { color })}
          >
            {account.initials?.slice(0, 2) || "?"}
          </div>
          <div className="t3-l0-card-body">
            <div className="t3-l0-card-name">
              {account.name}
              {capWarn ? (
                <span
                  style={{
                    color: fanTone === "over" ? "#ef4444" : "#f59e0b",
                    fontWeight: 700,
                  }}
                  title="Fan-out capacity warning"
                >
                  {capWarn}
                </span>
              ) : null}
            </div>
            <div className="t3-l0-card-meta">{getAccountStatus(account)}</div>
            <div className="t3-l0-card-stats">
              <span>
                <b>{account.myMappedPairs ?? 0}</b> {statsLabel}
              </span>
              <span>
                <b>{account.myAssessedPairs ?? 0}</b> assessed
              </span>
              <span>
                <b>{account.myPendingAssessments ?? 0}</b> pending
              </span>
            </div>
          </div>
          <AccountAlRlPills account={account} />
        </button>
      );
    };

    const sharedStatsLabel = teamGraphActive
      ? `${teamViewerFirstName || "Their"} mapped`
      : "your mapped";

    return (
      <>
        <div
          style={{ fontSize: 12, fontWeight: 700, color: "var(--gray-800)" }}
        >
          Portfolio summary
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: "var(--text-muted)",
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {stats.accounts} accounts · {stats.contactsMapped} contacts you map ·{" "}
          {stats.pending ? (
            <span style={{ color: "var(--amber)", fontWeight: 600 }}>
              {stats.pending} pending
            </span>
          ) : (
            "0 pending"
          )}
        </div>
        <div className="t3-l0-summary-row">
          <span className="t3-l0-sort">
            Sort by{" "}
            <select
              value={sortBy}
              onChange={event => setSortBy(event.target.value)}
            >
              <option value="aggregate_rl">Aggregate RL</option>
              <option value="name">Name</option>
              <option value="fanout">Fan-out</option>
              <option value="pending">Pending</option>
            </select>
          </span>
          <span
            style={{ fontSize: 10.5, color: "var(--brand)", fontWeight: 600 }}
          >
            Map: {visibleStart}-{visibleEnd} of {sortedAccounts.length}
          </span>
        </div>
        <div>
          {sidebarSharedAccounts.length > 0 && (
            <div
              style={{
                border: "1.5px solid #6ee7b7",
                borderRadius: 10,
                background: "#f0fdf9",
                padding: "6px 6px 2px",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#065f46",
                  letterSpacing: "0.01em",
                  marginBottom: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {(teamsSelectedUserName || "").split(" ")[0]} as Member
              </div>
              {sidebarSharedAccounts.map(account =>
                renderSidebarAccountRow(account, {
                  statsLabel: sharedStatsLabel,
                })
              )}
            </div>
          )}
          {sidebarOtherAccounts.map(account =>
            renderSidebarAccountRow(account)
          )}
        </div>
        {sortedAccounts.length === 0 ? (
          <div
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              padding: "10px 0",
            }}
          >
            No accounts match your search.
          </div>
        ) : null}
        <div
          style={{
            marginTop: 14,
            fontSize: 10.5,
            color: "var(--text-muted)",
            lineHeight: 1.55,
          }}
        >
          Click an account to peek its detail here. The strip above lists your
          full portfolio; this list mirrors the current map page.
        </div>
      </>
    );
  };

  if (!portfolioAccounts.length) {
    return <MyRelationshipsEmptyState />;
  }

  return (
    <div className="rl-prototype-skin">
      <section className="acct-picker-panel" aria-label="Your accounts">
        <header className="acct-picker-hdr">
          <div className="acct-picker-hdr-title">
            <Building2 className="acct-picker-hdr-icon" aria-hidden />
            <div>
              <div className="acct-picker-hdr-label">
                {comparisonActive ? "Compared accounts" : "Portfolio Accounts"}
              </div>
              <div className="acct-picker-hdr-sub">
                {searchQuery
                  ? `${stripAccounts.length} of ${portfolioAccounts.length} shown`
                  : comparisonActive
                    ? `${stripAccounts.length} selected for comparison`
                    : `${portfolioAccounts.length} Account${portfolioAccounts.length === 1 ? "" : "s"} in this Portfolio`}
              </div>
            </div>
          </div>
          {onTeamsRelationshipToggle ? (
            <div
              style={{
                marginLeft: "auto",
                marginRight: 8,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ToggleSwitch
                id="rl-teams-relationship-toggle-inline"
                label="Team Relationship"
                checked={teamsRelationshipToggle}
                onChange={onTeamsRelationshipToggle}
              />
              {teamsRelationshipToggle && teamsSelectedUserName ? (
                <button
                  type="button"
                  onClick={onOpenTeamModal}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 11px",
                    borderRadius: 9999,
                    border: "1.5px solid #7c3aed",
                    background: "#f5f3ff",
                    color: "#6d28d9",
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                  title="Switch team member"
                >
                  <Users style={{ width: 13, height: 13 }} aria-hidden />
                  <span style={{ color: "#9ca3af", fontWeight: 500 }}>
                    Select Team Members:
                  </span>
                  {teamsSelectedUserName}
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="acct-picker-search">
            <Search className="acct-picker-search-icon" aria-hidden />
            <input
              type="text"
              role="searchbox"
              className="acct-picker-search-input"
              placeholder="Search accounts, contacts, status…"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              aria-label="Search your accounts"
            />
            {searchQuery ? (
              <button
                type="button"
                className="acct-picker-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <X style={{ width: 14, height: 14 }} aria-hidden />
              </button>
            ) : null}
          </div>
        </header>

        <div
          className={`acct-picker-scroll-outer${canScrollLeft ? " can-scroll-left" : ""}${canScrollRight ? " can-scroll-right" : ""}`}
        >
          <button
            type="button"
            className="acct-picker-nav acct-picker-nav--prev"
            aria-label="Scroll accounts left"
            disabled={!canScrollLeft}
            onClick={() => scrollAccountCards(-1)}
          >
            <ChevronLeft style={{ width: 18, height: 18 }} aria-hidden />
          </button>

          <div className="acct-picker-scroll" ref={cardsScrollRef}>
            <div className="acct-picker-track">
              {isViewingTeamMember && !comparisonActive ? (
                sharedAccounts.length === 0 && myOnlyAccounts.length === 0 ? (
                  <div className="acct-picker-empty">
                    <p>No accounts match &ldquo;{searchQuery.trim()}&rdquo;</p>
                    {searchQuery ? (
                      <button type="button" onClick={() => setSearchQuery("")}>
                        Clear search
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    {sharedAccounts.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexShrink: 0,
                          border: "1.5px solid #6ee7b7",
                          borderRadius: 10,
                          background: "#f0fdf9",
                          padding: "6px 6px 8px",
                          gap: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: "#065f46",
                            letterSpacing: "0.01em",
                            marginBottom: 8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(teamsSelectedUserName || "").split(" ")[0]} as
                          Member
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                          {sharedAccountsForLens.map(account => (
                            <AccountPickerCard
                              key={account.id}
                              account={account}
                              variant="my-relationships"
                              youLabel={
                                teamGraphActive
                                  ? teamViewerFirstName || "You"
                                  : "You"
                              }
                              isSelected={false}
                              isPeek={peekAccountId === account.id}
                              isCompareSelected={selectedAccountIds.includes(
                                account.id
                              )}
                              onClick={event =>
                                handleAccountIntent(account.id, event)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {sharedAccounts.length > 0 && myOnlyAccounts.length > 0 && (
                      <div
                        style={{
                          width: 1,
                          alignSelf: "stretch",
                          background: "#e5e7eb",
                          flexShrink: 0,
                          margin: "20px 2px 4px",
                        }}
                      />
                    )}
                    {myOnlyAccounts.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexShrink: 0,
                          alignSelf: "flex-end",
                          paddingBottom: sharedAccounts.length > 0 ? 9 : 0,
                        }}
                      >
                        {myOnlyAccounts.map(account => (
                          <AccountPickerCard
                            key={account.id}
                            account={account}
                            variant="my-relationships"
                            isSelected={false}
                            isPeek={peekAccountId === account.id}
                            isCompareSelected={selectedAccountIds.includes(
                              account.id
                            )}
                            disabled={restrictToSharedAccounts}
                            onClick={event =>
                              handleAccountIntent(account.id, event)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </>
                )
              ) : stripAccounts.length === 0 ? (
                <div className="acct-picker-empty">
                  <p>No accounts match &ldquo;{searchQuery.trim()}&rdquo;</p>
                  {searchQuery ? (
                    <button type="button" onClick={() => setSearchQuery("")}>
                      Clear search
                    </button>
                  ) : null}
                </div>
              ) : (
                stripAccounts.map(account => (
                  <AccountPickerCard
                    key={account.id}
                    account={account}
                    variant="my-relationships"
                    youLabel={
                      teamGraphActive ? teamViewerFirstName || "You" : "You"
                    }
                    isSelected={
                      comparisonActive && focusedCompareAccountId === account.id
                    }
                    isPeek={!comparisonActive && peekAccountId === account.id}
                    isCompareSelected={
                      !comparisonActive &&
                      selectedAccountIds.includes(account.id)
                    }
                    onClick={event => handleAccountIntent(account.id, event)}
                  />
                ))
              )}
            </div>
          </div>

          <button
            type="button"
            className="acct-picker-nav acct-picker-nav--next"
            aria-label="Scroll accounts right"
            disabled={!canScrollRight}
            onClick={() => scrollAccountCards(1)}
          >
            <ChevronRight style={{ width: 18, height: 18 }} aria-hidden />
          </button>
        </div>
      </section>

      <div className="t3-toolbar">
        {isViewingTeamMember && !comparisonActive ? (
          <ToggleSwitch
            id="view-my-graph-toggle"
            label="View My Graph"
            checked={viewMyGraph}
            onChange={val => onToggleViewMyGraph?.(val)}
          />
        ) : (
          <span className="t3-layer-chip">
            {comparisonActive
              ? `Comparing ${selectedAccountIds.length} accounts`
              : "Portfolio overview (your accounts)"}
          </span>
        )}
        <div
          className={`t3-cmp-cluster ${isCompareMode ? "compare-on" : ""}`}
          style={comparisonActive ? { display: "none" } : undefined}
        >
          <button
            type="button"
            className={`t3-cmp-btn ${isCompareMode ? "active" : ""}`}
            onClick={toggleCompareMode}
          >
            {isCompareMode ? "Cancel compare" : "Compare accounts"}
          </button>
          {selectedAccountIds.length > 0 && (
            <>
              <span className="t3-sel-count">
                {selectedAccountIds.length} selected
              </span>
              <button
                type="button"
                className="t3-sel-clear"
                onClick={clearSelection}
              >
                x Clear
              </button>
              <button
                type="button"
                className="t3-cmp-cta"
                disabled={selectedAccountIds.length < 2}
                title={
                  selectedAccountIds.length < 2
                    ? "Select 1 more account to compare"
                    : ""
                }
                onClick={() => {
                  if (selectedAccountIds.length < 2) return;
                  setPeekAccountId(null);
                  setFocusedCompareAccountId(null);
                  setComparisonActive(true);
                  setMode("portfolio");
                }}
              >
                Compare {selectedAccountIds.length}
              </button>
            </>
          )}
          {isCompareMode && (
            <span className="t3-presel-wrap">
              <label className="t3-presel-lbl" htmlFor="t3-presel-sel">
                Auto-pick:
              </label>
              <select
                id="t3-presel-sel"
                className="t3-presel-sel"
                value={autoPick}
                onChange={event => applyAutoPick(event.target.value)}
              >
                {AUTO_PICK_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </span>
          )}
          {autoPickNote && (
            <span className="t3-presel-note">{autoPickNote}</span>
          )}
        </div>
        {(comparisonActive || peekAccountId) && (
          <button
            type="button"
            className="t3-back-btn"
            onClick={() => {
              setComparisonActive(false);
              setFocusedCompareAccountId(null);
              setMode("portfolio");
              setPeekAccountId(null);
            }}
          >
            &larr; Back to portfolio
          </button>
        )}
        <label className="t3-page-size">
          Show
          <select
            value={pageSize}
            onChange={event => setPageSize(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        {!comparisonActive ? (
          <span className="t3-toolbar-hint">
            {isCompareMode && selectedAccountIds.length === 0
              ? "Click accounts in the strip above to add 2–8, or Shift-click anytime"
              : selectedAccountIds.length === 1
                ? "Select 1 more account in the strip to compare"
                : "Tip: Shift-click an account in the strip to add it to Compare"}
          </span>
        ) : null}
      </div>

      <div className="t3-stats-bar">
        <div
          className="t3-sc"
          title={
            comparisonActive
              ? teamGraphActive
                ? `${teamViewerFirstName || "Teammate"}↔contact pairs they are mapped to, within the ${selectedAccountIds.length} compared accounts (assessed + pending)`
                : `You↔contact pairs you are mapped to, within the ${selectedAccountIds.length} compared accounts (assessed + pending)`
              : teamGraphActive
                ? `${teamViewerFirstName || "Teammate"}↔contact pairs they are mapped to, across their portfolio (assessed + pending)`
                : "You↔contact pairs you are mapped to, across your portfolio (assessed + pending)"
          }
        >
          <span className="t3-sc-val">{stats.totalPairs}</span>
          <span className="t3-sc-lbl">
            {comparisonActive
              ? `Compared (${selectedAccountIds.length})`
              : teamGraphActive
                ? teamViewerFirstName || "Their"
                : "My"}{" "}
            Pairs
          </span>
        </div>
        <div className="t3-sep" />
        <div
          className="t3-sc"
          title={
            comparisonActive
              ? `Mapped pairs with an RL assessment captured, within the ${selectedAccountIds.length} compared accounts. Pending pairs excluded.`
              : "Mapped pairs with an RL assessment captured, across your portfolio. Pending pairs excluded."
          }
        >
          <span className="t3-sc-val t3-sc-val--assessed">
            {stats.assessed}
          </span>
          <span className="t3-sc-lbl">Assessed</span>
        </div>
        <div className="t3-sep" />
        <div
          className="t3-sc"
          title={
            comparisonActive
              ? `Assessed pairs at RL 4–5 (Strong band), within the ${selectedAccountIds.length} compared accounts. Pair-level count — not an account-level RL band.`
              : "Assessed pairs at RL 4–5 (Strong band), across your portfolio. Pair-level count — not an account-level RL band."
          }
        >
          <span className="t3-sc-val t3-sc-val--strong">
            {stats.strongPairs}
          </span>
          <span className="t3-sc-lbl">Strong (RL 4+)</span>
        </div>
        <div className="t3-sep" />
        <div
          className="t3-sc"
          title={
            comparisonActive
              ? `Assessed pairs at RL 0–1 (Weak band), within the ${selectedAccountIds.length} compared accounts. Pending pairs excluded. Pair-level count — not an account-level RL band.`
              : "Assessed pairs at RL 0–1 (Weak band), across your portfolio. Pending pairs excluded. Pair-level count — not an account-level RL band."
          }
        >
          <span className="t3-sc-val t3-sc-val--risk">{stats.atRiskPairs}</span>
          <span className="t3-sc-lbl">At Risk</span>
        </div>
        <div className="t3-sep" />
        <div
          className="t3-cap"
          title={
            (fanOutHighlightAccount
              ? `Fan-out for ${fanOutHighlightAccount.name || "this account"}: ${fanOutCount} contact(s) mapped out of ${fanOutCap} per-account capacity.`
              : teamGraphActive
                ? `Highest fan-out across ${teamViewerFirstName || "their"} portfolio: ${fanOutCount} contact(s) mapped per account (max), out of ${fanOutCap} per-account capacity.`
                : `Highest fan-out across your portfolio: ${fanOutCount} contact(s) mapped per account (max), out of ${fanOutCap} per-account capacity.`) +
            ` Portfolio limit for your grade: ${fanOutPortfolioCap}.`
          }
        >
          <span className="t3-cap-lbl">
            Fan-out{" "}
            <span style={{ fontWeight: 400, opacity: 0.7 }}>
              ({fanOutScopeLabel})
            </span>
          </span>
          <span
            className="t3-cap-num"
            style={{
              color: fanCapColor(fanOutTone),
            }}
          >
            {fanOutCount}/{fanOutCap}
          </span>
          <div className="t3-mini-bar">
            <div
              className="t3-mini-bar-fill"
              style={{
                width: `${Math.min(100, fanOutCap ? (fanOutCount / fanOutCap) * 100 : 0)}%`,
                background: fanCapColor(fanOutTone),
              }}
            />
          </div>
        </div>
        <div className="t3-stats-action">
          <button
            type="button"
            className="btn btn-sm btn-sec"
            style={{
              fontSize: 11,
              whiteSpace: "nowrap",
              ...exploreAllRelationshipsBtnStyle,
            }}
            title={
              EXPLORE_ALL_RELATIONSHIPS_ENABLED
                ? undefined
                : EXPLORE_ALL_RELATIONSHIPS_COMING_SOON_MSG
            }
            aria-disabled={
              EXPLORE_ALL_RELATIONSHIPS_ENABLED ? undefined : "true"
            }
            onClick={onExploreAllRelationships}
          >
            Explore All Relationships
          </button>
        </div>
      </div>

      <div className="t3-main-panel">
        <div
          className={`t3-map-panel ${mode === "compare" && !comparisonActive ? "compare-mode" : ""}`}
        >
          {comparisonActive ? (
            <MyRelationshipsL1CompareMap
              accounts={compareAccountsResolved}
              focusedAccountId={focusedCompareAccountId}
              onFocusAccount={handleCompareFocus}
              centerLabel={compareCenterLabel}
              centerUserStakeholder={
                teamGraphActive
                  ? teamMemberCenterStakeholder
                  : currentUserStakeholder
              }
            />
          ) : (
            <MyRelationshipsL0Map
              accounts={
                teamGraphActive && teamGraphSharedAccounts.length > 0
                  ? teamGraphSharedAccounts
                  : pageAccounts
              }
              considerMeddpiccRole={considerMeddpiccRole}
              activeAccountId={peekAccountId}
              selectedAccountIds={selectedAccountIds}
              onSelectAccount={handleAccountIntent}
              onClearPeek={() => setPeekAccountId(null)}
              onOpenAccount={onSelectAccount}
              onAssessRingContact={handleAssessRingContact}
              userName={
                teamGraphActive
                  ? (teamMemberCenterStakeholder?.name ??
                    teamsSelectedUserName ??
                    "")
                  : relationshipUserName || "You"
              }
              centerUserStakeholder={
                teamGraphActive
                  ? teamMemberCenterStakeholder
                  : currentUserStakeholder
              }
              sharedAccountIds={
                isViewingTeamMember && !teamGraphActive ? sharedAccountIds : []
              }
              isViewingTeamMember={isViewingTeamMember && !teamGraphActive}
              peekAccounts={
                teamGraphActive && teamMemberGraphAccounts.length > 0
                  ? teamMemberGraphAccounts
                  : undefined
              }
              peekUserName={
                teamGraphActive
                  ? (teamMemberCenterStakeholder?.name ??
                    teamsSelectedUserName ??
                    "")
                  : undefined
              }
              peekCenterUserStakeholder={
                teamGraphActive ? teamMemberCenterStakeholder : undefined
              }
              fanOutCap={fanOutCap}
            />
          )}
        </div>
        <div className="t3-sidebar-panel">
          <div className="t3-sb-body">
            {focusedCompareAccount
              ? renderAccountDetail(focusedCompareAccount)
              : peekAccount
                ? renderAccountDetail(peekAccount)
                : comparisonActive && selectedAccountIds.length > 0
                  ? renderComparisonSidebar()
                  : renderPortfolioSidebar()}
          </div>
        </div>
      </div>

      {!comparisonActive && totalPages > 1 ? (
        <div className="t3-pager t3-pager--compact">
          <span className="t3-page-status">
            Map page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="t3-back-btn"
            onClick={() => setPage(current => Math.max(1, current - 1))}
            disabled={safePage <= 1}
          >
            Previous
          </button>
          <button
            type="button"
            className="t3-back-btn"
            onClick={() =>
              setPage(current => Math.min(totalPages, current + 1))
            }
            disabled={safePage >= totalPages}
          >
            Next
          </button>
        </div>
      ) : null}

      <RlAssessmentModal
        open={Boolean(assessmentTarget)}
        pair={assessmentTarget?.pair}
        stakeholder={assessmentTarget?.stakeholder}
        contact={assessmentTarget?.contact}
        executiveId={assessmentExecutiveId}
        onClose={() => setAssessmentTarget(null)}
        onSubmitted={handleAssessmentSubmitted}
      />
    </div>
  );
}
